// src/components/KeyValuesDashboard/components/tabs/AdUnitTab.tsx

import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Monitor, Target } from 'lucide-react';

import type { ComparisonResponse, AdUnitData } from '@/types/analytics';
import {
  formatCurrency,
  formatNumber,
  getMetricTotal,
  truncateText,
} from '@/utils/keyvalues-helpers';

interface AdUnitTabProps {
  data: ComparisonResponse;
  mode: 'all' | 'compare';
}

export function AdUnitTab({ data}: AdUnitTabProps) {
  const [selectedAdUnit, setSelectedAdUnit] = useState<AdUnitData | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<'revenue' | 'impressions' | 'clicks'>('revenue');

  const adUnitData = data.ad_unit_breakdown || [];

  // Preparar dados para gráfico
  const chartData = adUnitData.map(au => {
    const totalMetric = au.values.reduce((sum, v) => 
      sum + getMetricTotal(v.metrics, selectedMetric), 0
    );

    return {
      name: truncateText(au.ad_unit_name, 25),
      fullName: au.ad_unit_name,
      value: totalMetric,
      revenue: au.total_revenue,
    };
  }).sort((a, b) => b.revenue - a.revenue).slice(0, 15);

  const formatYAxis = (value: number) => {
    switch (selectedMetric) {
      case 'revenue':
        return `$${(value / 1000).toFixed(0)}k`;
      case 'impressions':
      case 'clicks':
        return `${(value / 1000).toFixed(0)}k`;
      default:
        return value.toString();
    }
  };

  const formatValue = (value: number) => {
    switch (selectedMetric) {
      case 'revenue':
        return formatCurrency(value);
      case 'impressions':
      case 'clicks':
        return formatNumber(value);
      default:
        return value.toString();
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Controles */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-indigo-600" />
            Performance por Ad Unit
          </h3>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Métrica:</span>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="revenue">Receita</option>
              <option value="impressions">Impressões</option>
              <option value="clicks">Cliques</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cards de Resumo - Top 3 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {adUnitData.slice(0, 3).map((adUnit, index) => {
          const colors = [
            'from-indigo-500 to-indigo-600',
            'from-violet-500 to-violet-600',
            'from-purple-500 to-purple-600',
          ];
          const bgColors = [
            'from-indigo-50 to-purple-50 border-indigo-200',
            'from-violet-50 to-purple-50 border-violet-200',
            'from-purple-50 to-pink-50 border-purple-200',
          ];

          const totalImpressions = adUnit.values.reduce((sum, v) => 
            sum + getMetricTotal(v.metrics, 'impressions'), 0
          );

          return (
            <div key={adUnit.ad_unit_name} className={`bg-gradient-to-br ${bgColors[index]} rounded-2xl p-6 border shadow-lg`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${colors[index]} shadow-lg`}>
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm">
                  <span className="text-sm font-bold text-slate-700">#{index + 1}</span>
                </div>
              </div>
              
              <p className="text-sm font-medium text-slate-700 mb-2 truncate" title={adUnit.ad_unit_name}>
                {adUnit.ad_unit_name}
              </p>
              <p className="text-2xl font-bold text-slate-900 mb-1">
                {formatCurrency(adUnit.total_revenue)}
              </p>
              <p className="text-xs text-slate-600">
                {formatNumber(totalImpressions)} impressões
              </p>
            </div>
          );
        })}
      </div>

      {/* Gráfico de Barras Horizontal */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">
          Top 15 Ad Units por {selectedMetric === 'revenue' ? 'Receita' : selectedMetric === 'impressions' ? 'Impressões' : 'Cliques'}
        </h3>
        
        <ResponsiveContainer width="100%" height={500}>
          <BarChart 
            data={chartData} 
            layout="vertical"
            margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              type="number" 
              tick={{ fontSize: 12, fill: '#64748b' }}
              tickFormatter={formatYAxis}
            />
            <YAxis 
              type="category" 
              dataKey="name" 
              tick={{ fontSize: 12, fill: '#64748b' }}
              width={140}
            />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-4 rounded-xl shadow-xl border border-slate-200">
                      <p className="font-semibold text-slate-900 mb-2">{data.fullName}</p>
                      <div className="space-y-1 text-sm">
                        <p className="text-slate-600">
                          {selectedMetric === 'revenue' ? 'Receita' : selectedMetric === 'impressions' ? 'Impressões' : 'Cliques'}:{' '}
                          <span className="font-semibold text-indigo-600">
                            {formatValue(data.value)}
                          </span>
                        </p>
                        <p className="text-slate-600">
                          Receita Total: <span className="font-semibold">{formatCurrency(data.revenue)}</span>
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar 
              dataKey="value" 
              fill="url(#colorAdUnit)" 
              radius={[0, 8, 8, 0]}
              onClick={(data) => {
                  //@ts-ignore
                const adUnit = adUnitData.find(au => au.ad_unit_name === data.fullName);
                if (adUnit) setSelectedAdUnit(adUnit);
              }}
              style={{ cursor: 'pointer' }}
            />
            <defs>
              <linearGradient id="colorAdUnit" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.6} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Ad Unit Selecionado - Detalhes */}
      {selectedAdUnit && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900">
              {selectedAdUnit.ad_unit_name}
            </h3>
            <button
              onClick={() => setSelectedAdUnit(null)}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Limpar seleção
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200">
              <p className="text-sm text-slate-600 mb-1">Receita Total</p>
              <p className="text-2xl font-bold text-indigo-900">
                {formatCurrency(selectedAdUnit.total_revenue)}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200">
              <p className="text-sm text-slate-600 mb-1">Impressões</p>
              <p className="text-2xl font-bold text-violet-900">
                {formatNumber(selectedAdUnit.values.reduce((sum, v) => 
                  sum + getMetricTotal(v.metrics, 'impressions'), 0
                ))}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
              <p className="text-sm text-slate-600 mb-1">Cliques</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatNumber(selectedAdUnit.values.reduce((sum, v) => 
                  sum + getMetricTotal(v.metrics, 'clicks'), 0
                ))}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-4 border border-pink-200">
              <p className="text-sm text-slate-600 mb-1">Nº de Valores</p>
              <p className="text-2xl font-bold text-pink-900">
                {selectedAdUnit.values.length}
              </p>
            </div>
          </div>

          {/* Tabela de valores por ad unit */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Receita
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Impressões
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                    Cliques
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {selectedAdUnit.values.map((value) => (
                  <tr key={value.custom_value} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {value.custom_value}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">
                      {formatCurrency(getMetricTotal(value.metrics, 'revenue'))}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {formatNumber(getMetricTotal(value.metrics, 'impressions'))}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {formatNumber(getMetricTotal(value.metrics, 'clicks'))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabela Completa */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Ranking Completo de Ad Units
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Ad Unit
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Receita Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Nº de Valores
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Ação
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {adUnitData.map((adUnit, index) => (
                <tr key={adUnit.ad_unit_name} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 font-semibold text-sm">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-900">
                    {adUnit.ad_unit_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-slate-900">
                    {formatCurrency(adUnit.total_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-slate-600">
                    {adUnit.values.length}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => setSelectedAdUnit(adUnit)}
                      className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
                    >
                      Ver detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
